import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export type CancellationReasonType =
  | "CUSTOMER_REQUEST"
  | "DOUBLE_BOOKING"
  | "FULLY_BOOKED"
  | "WORKER_UNAVAILABLE"
  | "SALON_CLOSED"
  | "WEATHER"
  | "EMERGENCY"
  | "RESCHEDULE_REQUEST"
  | "SERVICE_UNAVAILABLE"
  | "OTHER";

interface EditCancellationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reasonType: CancellationReasonType, reasonText: string) => void;
  initialReasonType: CancellationReasonType;
  initialReasonText: string;
  isLoading?: boolean;
}

export function EditCancellationDialog({
  open,
  onClose,
  onConfirm,
  initialReasonType,
  initialReasonText,
  isLoading = false,
}: EditCancellationDialogProps) {
  const { t } = useTranslation("appointments");
  const [reasonType, setReasonType] = useState<CancellationReasonType>(initialReasonType);
  const [reasonText, setReasonText] = useState(initialReasonText);

  // Update local state when initial values change
  useEffect(() => {
    if (open) {
      setReasonType(initialReasonType);
      setReasonText(initialReasonText);
    }
  }, [open, initialReasonType, initialReasonText]);

  const handleConfirm = () => {
    onConfirm(reasonType, reasonText);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("editCancellation.title")}</DialogTitle>
          <DialogDescription>{t("editCancellation.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason-type">{t("decline.reasonLabel")}</Label>
            <Select
              value={reasonType}
              onValueChange={(value) => setReasonType(value as CancellationReasonType)}
              disabled={isLoading}
            >
              <SelectTrigger id="reason-type">
                <SelectValue>
                  {reasonType ? t(`decline.reasons.${reasonType}`) : t("decline.reasonPlaceholder")}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CUSTOMER_REQUEST">
                  {t("decline.reasons.CUSTOMER_REQUEST")}
                </SelectItem>
                <SelectItem value="DOUBLE_BOOKING">
                  {t("decline.reasons.DOUBLE_BOOKING")}
                </SelectItem>
                <SelectItem value="FULLY_BOOKED">{t("decline.reasons.FULLY_BOOKED")}</SelectItem>
                <SelectItem value="WORKER_UNAVAILABLE">
                  {t("decline.reasons.WORKER_UNAVAILABLE")}
                </SelectItem>
                <SelectItem value="SALON_CLOSED">{t("decline.reasons.SALON_CLOSURE")}</SelectItem>
                <SelectItem value="WEATHER">{t("decline.reasons.WEATHER")}</SelectItem>
                <SelectItem value="EMERGENCY">{t("decline.reasons.EMERGENCY")}</SelectItem>
                <SelectItem value="RESCHEDULE_REQUEST">
                  {t("decline.reasons.RESCHEDULE_REQUEST")}
                </SelectItem>
                <SelectItem value="SERVICE_UNAVAILABLE">
                  {t("decline.reasons.SERVICE_UNAVAILABLE")}
                </SelectItem>
                <SelectItem value="OTHER">{t("decline.reasons.OTHER")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason-text">{t("decline.additionalDetailsLabel")}</Label>
            <Textarea
              id="reason-text"
              placeholder={t("decline.additionalDetailsPlaceholder")}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              disabled={isLoading}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? t("editCancellation.saving") : t("editCancellation.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
